'use client';
import { useState, useEffect, useCallback } from 'react';
import LightboxOverlay, { LB_SIZES, LB_WIDTH } from './LightboxOverlay';
import { avatarImg, wideImg } from '@/lib/imgUrl';
import type { SeoCtx } from '@/lib/imageSeo';

type Props = {
  images: string[];
  variant?: 'row' | 'grid';
  /** pixel width requested for the grid thumbnails (default 110 = the narrow cards; the desktop gallery asks for more) */
  thumbSize?: number;
  referrerPolicy?: 'no-referrer';
  max?: number;
  /** Descriptive base for alt text, e.g. "אברהם - שמאי מקרקעין" — each thumbnail gets
   *  "<altBase> - תמונה N מתוך M" instead of empty/generic alt (SEO + a11y). */
  altBase?: string;
  /** IMAGE-SEO: category/service/pro context → keyword alt + title + /pic/<size>/<b64>/<slug>.webp URLs (lib/imageSeo.ts). */
  seoCtx?: SeoCtx;
  seoKind?: 'gallery' | 'review';
  /** Hebrew tag labels per image, ALIGNED with `images` (lib/imageSeoServer.galleryTagLabels). */
  imageTags?: string[][];
};

export default function ImageLightbox({ images, variant = 'row', referrerPolicy, max, altBase, seoCtx, seoKind = 'gallery', imageTags, thumbSize = 110 }: Props) {
  // `max` limits the THUMBNAILS only — the lightbox always walks the whole gallery (Founder rebuke 2026-09-22:
  // a 36-photo gallery showed 6 and the other 30 were unreachable). The last thumbnail carries a "+N" badge.
  const list = images;
  const shown = max ? images.slice(0, max) : images;
  const hiddenCount = list.length - shown.length;
  const altFor = (i: number) => altBase ? `${altBase} - תמונה ${i + 1} מתוך ${list.length}` : `תמונה ${i + 1} מתוך ${list.length}`;
  const seoFor = (i: number) => seoCtx ? ({ kind: seoKind, ctx: { ...seoCtx, tags: imageTags?.[i] || null, n: i + 1 } }) : undefined;
  const [idx, setIdx] = useState<number | null>(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => setMounted(true), []);

  const close = useCallback(() => setIdx(null), []);
  const go = useCallback((d: number) => setIdx(i => (i === null ? i : (i + d + list.length) % list.length)), [list.length]);

  if (!list.length) return null;

  const thumbs = variant === 'grid' ? (
    <div className="grid grid-cols-3 gap-1.5">
      {shown.map((img, i) => (
        <button key={i} onClick={() => setIdx(i === shown.length - 1 && hiddenCount > 0 ? shown.length : i)} type="button" /* "+N" opens the first HIDDEN photo */
          className="block rounded-lg overflow-hidden relative"
          style={{ aspectRatio: '1', padding: 0, border: 'none', cursor: 'pointer', background: '#f1f5f9' }}
          aria-label={i === shown.length - 1 && hiddenCount > 0 ? `עוד ${hiddenCount} תמונות` : altFor(i)}>
          <img {...avatarImg(img, thumbSize, seoFor(i))} {...(seoCtx ? {} : { alt: altFor(i) })} referrerPolicy={referrerPolicy} loading="lazy" decoding="async"
            className="object-cover hover:opacity-90 transition-opacity"
            style={{ width: '100%', height: '100%' }} />
          {i === shown.length - 1 && hiddenCount > 0 && (
            <span className="absolute inset-0 flex items-center justify-center text-white font-bold"
              style={{ background: 'rgba(15,23,42,.55)', fontSize: 22 }}>+{hiddenCount}</span>
          )}
        </button>
      ))}
    </div>
  ) : (
    <div className="flex gap-2 mt-3 flex-wrap">
      {list.map((img, i) => (
        <button key={i} onClick={() => setIdx(i)} type="button" aria-label={altFor(i)}
          className="block rounded-xl overflow-hidden"
          style={{ width: 80, height: 80, padding: 0, border: 'none', cursor: 'pointer', background: '#f1f5f9' }}>
          <img {...avatarImg(img, 80, seoFor(i))} {...(seoCtx ? {} : { alt: altFor(i) })} referrerPolicy={referrerPolicy} loading="lazy" decoding="async"
            className="object-cover w-full h-full hover:opacity-90 transition-opacity" />
        </button>
      ))}
    </div>
  );

  const overlay = idx !== null && mounted ? (
    <LightboxOverlay count={list.length} idx={idx} go={go} close={close}
      imgProps={i => ({ ...wideImg(list[i], LB_SIZES, LB_WIDTH, seoFor(i)), ...(seoCtx ? {} : { alt: altFor(i) }), referrerPolicy })} />
  ) : null;

  return <>{thumbs}{overlay}</>;
}
